// src/client/screens/auth/LoginScreen.tsx
// Two-step login: phone → OTP.
// VITE_DEV_LOGIN=true: phone step only — calls dev-login Edge Function to sign
// in immediately with a real session, no OTP required. Use for test accounts.
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, KeyRound } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { OTP_LENGTH, PHONE_COUNTRY_CODE } from '@/constants/config';
import Button from '@client/components/Button';
import Logo from '@client/components/Logo';
import { useToast } from '@client/components/ToastProvider';
import { sendOtp, verifyOtp, devLogin } from '@client/services/authService';
import { isValidZambiaPhone, normalizeZambiaPhone, formatZambiaPhone } from '@client/utils/validation';

// Safe read: import.meta.env is undefined under ts-jest/ESM.
type ViteMeta = { env?: Record<string, string | undefined> };
const DEV_LOGIN = ((import.meta as ViteMeta).env?.['VITE_DEV_LOGIN']) === 'true';

type Step = 'phone' | 'otp';

// Shared layout values
const CARD_STYLE: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 20,
  padding: 24,
  boxShadow: '0 4px 24px rgba(15, 26, 44, 0.08)',
};

export default function LoginScreen() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toast = useToast();

  const handleSendOtp = async () => {
    if (!isValidZambiaPhone(phone)) {
      toast.show('Enter a valid +260 number', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (DEV_LOGIN) {
        // Dev mode: bypass OTP — creates a real session via the dev-login Edge Function.
        const r = await devLogin(phone);
        if (!r.ok) {
          const msg = r.error === 'invalid_phone' ? 'Invalid phone number' : 'Dev sign-in failed';
          toast.show(msg, 'error');
          return;
        }
        const next = params.get('next') ?? ROUTES.home;
        navigate(next, { replace: true });
      } else {
        const r = await sendOtp(phone);
        if (!r.ok) {
          const msg =
            r.error === 'invalid_phone' ? 'That number is not valid' :
            r.error === 'rate_limited' ? 'Too many attempts — please wait' :
            'Could not send code — please try again';
          toast.show(msg, 'error');
          return;
        }
        toast.show('Code sent', 'success');
        setStep('otp');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async () => {
    const normalized = normalizeZambiaPhone(phone);
    if (!normalized) { toast.show('Invalid phone', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await verifyOtp(normalized, code);
      if (!r.ok) {
        const msg = r.error === 'invalid_code'
          ? 'That code is not correct'
          : r.error === 'invalid_phone'
          ? 'Phone number is not valid'
          : 'Could not verify';
        toast.show(msg, 'error');
        return;
      }
      const next = params.get('next') ?? ROUTES.home;
      navigate(next, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  // Formatted phone for display on the OTP step
  const displayPhone = formatZambiaPhone(normalizeZambiaPhone(phone) ?? phone);

  return (
    <div style={{
      minHeight: '100dvh',
      background: 'var(--c-bg)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '24px 16px',
      gap: 24,
      maxWidth: 480,
      margin: '0 auto',
    }}>

      {/* ── Header: logo card + app name + tagline ── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 16,
          boxShadow: '0 2px 12px rgba(15, 26, 44, 0.08)',
          display: 'inline-flex',
        }}>
          <Logo size={48} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ margin: 0, fontWeight: 800, fontSize: 28, color: 'var(--c-text)' }}>Thandizo</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--c-text-muted)' }}>
            Microloans that actually help
          </p>
        </div>
      </div>

      {/* ── Form card ── */}
      {step === 'phone' ? (
        <div style={CARD_STYLE}>
          <label
            htmlFor="phone-input"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 8 }}
          >
            Phone Number
          </label>

          {/* Input row: phone icon + country code prefix + digits */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 12,
            border: '1.5px solid var(--c-border)',
            background: 'var(--c-surface-alt)',
            overflow: 'hidden',
            marginBottom: 16,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '13px 12px',
              color: 'var(--c-text-muted)',
              borderRight: '1.5px solid var(--c-border)',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}>
              <Phone size={15} strokeWidth={2} />
              <span style={{ fontSize: 14, fontWeight: 600 }}>{PHONE_COUNTRY_CODE}</span>
            </div>
            <input
              id="phone-input"
              inputMode="tel"
              placeholder="97 123 4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{
                flex: 1,
                padding: '14px 12px',
                fontSize: 16,
                border: 'none',
                background: 'transparent',
                outline: 'none',
                color: 'var(--c-text)',
              }}
            />
          </div>

          <Button block size="lg" loading={submitting} onClick={handleSendOtp}>
            {DEV_LOGIN ? 'Sign In' : 'Send OTP'}
          </Button>

          {DEV_LOGIN && (
            <p style={{ margin: '10px 0 0', fontSize: 11, textAlign: 'center', color: 'var(--c-text-dim)' }}>
              Dev mode — OTP bypassed
            </p>
          )}
        </div>
      ) : (
        <div style={CARD_STYLE}>
          {/* Subtitle: "Enter the 6-digit code sent to [phone]" */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--c-text-sec)' }}>
              Enter the 6-digit code sent to
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 15, fontWeight: 700, color: 'var(--c-text)' }}>
              {displayPhone}
            </p>
          </div>

          <label
            htmlFor="otp-input"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 8 }}
          >
            {`Enter the ${OTP_LENGTH}-digit code`}
          </label>

          {/* Input row: key icon + spaced digit input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 12,
            border: '1.5px solid var(--c-border)',
            background: 'var(--c-surface-alt)',
            overflow: 'hidden',
            marginBottom: 16,
          }}>
            <div style={{
              padding: '13px 12px',
              color: 'var(--c-text-muted)',
              borderRight: '1.5px solid var(--c-border)',
              display: 'flex',
              alignItems: 'center',
            }}>
              <KeyRound size={15} strokeWidth={2} />
            </div>
            <input
              id="otp-input"
              inputMode="numeric"
              maxLength={OTP_LENGTH}
              placeholder="0  0  0  0  0  0"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{
                flex: 1,
                padding: '14px 12px',
                fontSize: 20,
                letterSpacing: 8,
                textAlign: 'center',
                border: 'none',
                background: 'transparent',
                outline: 'none',
                color: 'var(--c-text)',
              }}
            />
          </div>

          <Button block size="lg" loading={submitting} onClick={handleVerify}>
            Verify &amp; Sign In
          </Button>

          <Button
            variant="ghost"
            style={{ marginTop: 8, width: '100%' }}
            onClick={() => { setStep('phone'); setCode(''); }}
          >
            Change phone number
          </Button>
        </div>
      )}
    </div>
  );
}
